import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildHierarchy(items, parentId = null) {
  let roots;
  if (parentId === null) {
    const withNull = items.filter(i => i.parentId === null);
    const minLvl = Math.min(...items.map(i => i.level));
    roots = withNull.length > 0 ? withNull : items.filter(i => i.level === minLvl);
  } else {
    roots = items.filter(i => i.parentId === parentId);
  }
  return roots
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(item => ({ ...item, children: buildHierarchy(items, item._id) }));
}

function countDescendants(node) {
  if (!node.children?.length) return 0;
  return node.children.reduce((s, c) => s + 1 + countDescendants(c), 0);
}

function flatIds(node) {
  const ids = [node._id];
  (node.children || []).forEach(c => ids.push(...flatIds(c)));
  return ids;
}

function findNodeInTree(nodes, id) {
  for (const n of nodes) {
    if (n._id === id) return n;
    const found = findNodeInTree(n.children || [], id);
    if (found) return found;
  }
  return null;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ChevronRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const ChevronDown = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const CheckCircle = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);
const CircleEmpty = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
  </svg>
);
const FolderIcon = ({ color = '#8B5CF6', open = false, size = 15 }) =>
  open ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
const TagIcon = ({ color = '#10B981', size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <circle cx="9" cy="9" r="2.2"/>
    <path d="M4 4h7l9 9-7 7-9-9V4z"/>
  </svg>
);

// ─── Sidebar tree row ────────────────────────────────────────────────────────

function SidebarNode({ node, depth, selectedId, onSelect, skillMap, expandedIds, onToggle }) {
  const isExpanded = expandedIds.has(node._id);
  const hasChildren = node.children?.length > 0;
  const isSelected = selectedId === node._id;
  const hasSkill = skillMap[node._id] || false;

  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded cursor-pointer text-sm select-none
          ${isSelected
            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
          }`}
        style={{ paddingTop: 3, paddingBottom: 3, paddingLeft: depth * 14 + 4, paddingRight: 6 }}
        onClick={() => onSelect(node)}
      >
        {/* chevron */}
        <span
          className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node._id); }}
        >
          {hasChildren
            ? (isExpanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>)
            : <span className="w-3 inline-block"/>
          }
        </span>

        {/* folder / tag icon */}
        <span className="flex-shrink-0">
          {hasChildren
            ? <FolderIcon color={node.color || '#8B5CF6'} open={isExpanded} size={14}/>
            : <TagIcon color={node.color || '#10B981'} size={12}/>
          }
        </span>

        {/* name */}
        <span className="truncate flex-1 min-w-0 ml-1 leading-snug">{node.name}</span>

        {/* green dot = has skill */}
        {hasSkill && (
          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500 ml-1" title="Má znalost"/>
        )}
      </div>

      {isExpanded && hasChildren && node.children.map(child => (
        <SidebarNode
          key={child._id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          skillMap={skillMap}
          expandedIds={expandedIds}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

// ─── Knowledge card ──────────────────────────────────────────────────────────

function KnowledgeCard({ node, hasSkill, isReadOnly, onToggle, onDrillDown, skillMap }) {
  const hasChildren = node.children?.length > 0;
  const totalDesc = countDescendants(node);
  const ownedDesc = totalDesc > 0
    ? flatIds(node).filter(id => id !== node._id && skillMap[id]).length
    : 0;
  const pct = totalDesc > 0 ? Math.round((ownedDesc / totalDesc) * 100) : null;

  return (
    <div
      className={`relative flex flex-col rounded-xl border-2 transition-all duration-150 overflow-hidden
        ${hasSkill
          ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20 shadow-sm'
          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm'
        }`}
    >
      {/* top color strip */}
      <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: node.color || '#8B5CF6' }}/>

      <div className="p-3 flex flex-col flex-1">
        {/* header */}
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5">
            {hasChildren
              ? <FolderIcon color={node.color || '#8B5CF6'} open={false} size={15}/>
              : <TagIcon color={node.color || '#10B981'} size={13}/>
            }
          </span>

          <span
            className={`flex-1 min-w-0 font-semibold text-sm leading-snug break-words
              ${hasChildren ? 'text-blue-600 dark:text-blue-400 cursor-pointer hover:underline' : 'text-gray-800 dark:text-gray-100 cursor-default'}
            `}
            onClick={() => hasChildren && onDrillDown(node)}
            title={hasChildren ? `Otevřít: ${node.name}` : undefined}
          >
            {node.name}
          </span>

          {/* toggle button */}
          {!isReadOnly && (
            <button
              onClick={() => onToggle(node._id, !hasSkill)}
              className={`flex-shrink-0 ml-1 transition-colors rounded-full
                ${hasSkill ? 'text-green-500 hover:text-green-700' : 'text-gray-300 dark:text-gray-600 hover:text-green-400'}`}
              title={hasSkill ? 'Kliknutím odebrat znalost' : 'Kliknutím označit jako zvládnuté'}
            >
              {hasSkill ? <CheckCircle size={17}/> : <CircleEmpty size={17}/>}
            </button>
          )}
        </div>

        {/* description */}
        {node.description && (
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 pl-5">
            {node.description}
          </p>
        )}

        {/* footer */}
        {(totalDesc > 0 || hasChildren) && (
          <div className="flex items-center justify-between mt-2 pl-5">
            {totalDesc > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                <div className="w-16 h-1 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                  <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }}/>
                </div>
                <span>{ownedDesc}/{totalDesc}</span>
              </div>
            )}
            {hasChildren && (
              <button
                onClick={() => onDrillDown(node)}
                className="text-xs text-blue-500 dark:text-blue-400 hover:underline ml-auto"
              >
                Otevřít →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function UserMindMapKnowledge({ userId, userName, isDarkMode, onClose, isReadOnly = false }) {
  const [knowledge, setKnowledge]         = useState([]);
  const [userKnowledge, setUserKnowledge] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');

  // Breadcrumb navigation: array of node objects
  const [navStack, setNavStack] = useState([]);

  // Sidebar expand state
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Edit skill modal
  const [editingNode, setEditingNode] = useState(null);
  const [editForm, setEditForm]       = useState({ hasSkill: false, notes: '' });

  // Add child panel
  const [addingChild, setAddingChild] = useState(null);
  const [addForm, setAddForm]         = useState({ name: '', color: '', description: '' });

  const predefinedColors = [
    { name: 'Fialová',          value: '#8B5CF6' },
    { name: 'Oranžová',         value: '#F59E0B' },
    { name: 'Zelená',           value: '#10B981' },
    { name: 'Modrá',            value: '#3B82F6' },
    { name: 'Červená',          value: '#EF4444' },
    { name: 'Růžová',           value: '#EC4899' },
    { name: 'Žlutá',            value: '#EAB308' },
    { name: 'Tyrkysová',        value: '#06B6D4' },
    { name: 'Fialovo-růžová',   value: '#A855F7' },
    { name: 'Limetkově zelená', value: '#84CC16' },
    { name: 'Oranžově červená', value: '#F97316' },
    { name: 'Šedá',             value: '#6B7280' },
    { name: 'Tmavě modrá',      value: '#1E40AF' },
    { name: 'Hnědá',            value: '#92400E' },
    { name: 'Tmavě zelená',     value: '#166534' },
  ];

  // ── data ─────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [kRes, ukRes] = await Promise.all([
        fetch(`${API_URL}/knowledge`),
        fetch(`${API_URL}/users/${userId}/knowledge`),
      ]);
      if (kRes.ok)  setKnowledge(await kRes.json());
      if (ukRes.ok) setUserKnowledge(await ukRes.json());
    } catch (err) {
      console.error('Error loading knowledge data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── derived ──────────────────────────────────────────────────────────────

  const hierarchy = buildHierarchy(knowledge);

  // id -> bool
  const skillMap = {};
  userKnowledge.forEach(uk => {
    const id = uk.knowledgeId?._id || uk.knowledgeId;
    if (id) skillMap[id] = uk.hasSkill || false;
  });

  const totalSkills = knowledge.length;
  const ownedSkills = Object.values(skillMap).filter(Boolean).length;
  const overallPct  = totalSkills > 0 ? Math.round((ownedSkills / totalSkills) * 100) : 0;

  const currentParent  = navStack[navStack.length - 1] ?? null;
  const currentChildren = currentParent
    ? (findNodeInTree(hierarchy, currentParent._id)?.children ?? [])
    : hierarchy;

  const filteredChildren = search.trim()
    ? currentChildren.filter(n => n.name.toLowerCase().includes(search.toLowerCase()))
    : currentChildren;

  // ── handlers ─────────────────────────────────────────────────────────────

  const drillDown = (node) => {
    setNavStack(prev => [...prev, node]);
    setSearch('');
    setExpandedIds(prev => new Set([...prev, node._id]));
  };

  const navTo = (index) => {
    setNavStack(prev => prev.slice(0, index));
    setSearch('');
  };

  const sidebarSelect = (node) => {
    // Navigate to that node and make it selected
    setNavStack([node]);
    setSearch('');
    setExpandedIds(prev => new Set([...prev, node._id]));
  };

  const toggleExpanded = (id) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSkill = async (knowledgeId, newValue) => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}/knowledge/${knowledgeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hasSkill: newValue,
          proficiencyLevel: newValue ? 'basic' : 'none',
          updatedBy: userId,
        }),
      });
      if (res.ok) await loadData();
    } catch (err) {
      console.error('Error saving user knowledge:', err);
    }
  };

  const openEdit = (node) => {
    const uk = userKnowledge.find(uk =>
      (uk.knowledgeId?._id || uk.knowledgeId) === node._id
    ) || { hasSkill: false, notes: '' };
    setEditingNode(node);
    setEditForm({ hasSkill: uk.hasSkill || false, notes: uk.notes || '' });
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}/knowledge/${editingNode._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hasSkill: editForm.hasSkill,
          proficiencyLevel: editForm.hasSkill ? 'basic' : 'none',
          notes: editForm.notes,
          updatedBy: userId,
        }),
      });
      if (res.ok) { await loadData(); setEditingNode(null); }
    } catch (err) {
      console.error('Error updating user skill:', err);
    }
  };

  const getDefaultColor = (type) =>
    ({ category: '#8B5CF6', subcategory: '#F59E0B', skill: '#10B981' }[type] || '#6B7280');

  const openAddChild = (parentNode) => {
    const nextType = parentNode.type === 'category' ? 'subcategory' : 'skill';
    setAddingChild(parentNode);
    setAddForm({ name: '', color: getDefaultColor(nextType), description: '' });
  };

  const confirmAddChild = async () => {
    if (!addForm.name.trim()) return;
    const nextType = addingChild.type === 'category' ? 'subcategory' : 'skill';
    try {
      const res = await fetch(`${API_URL}/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: addForm.name.trim(),
          type: nextType,
          parentId: addingChild._id,
          color: addForm.color,
          description: addForm.description,
          order: knowledge.filter(k => k.parentId === addingChild._id).length + 1,
        }),
      });
      if (res.ok) { await loadData(); setAddingChild(null); }
    } catch (err) {
      console.error('Error adding knowledge:', err);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 flex flex-col items-center shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"/>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">
            Načítám znalosti {userName}…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col z-50" style={{ background: isDarkMode ? '#111827' : '#F3F4F6' }}>

      {/* ══ TOP BAR ════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center gap-3 shadow-sm">

        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
          Zpět
        </button>

        <div className="text-lg font-bold text-gray-900 dark:text-white truncate flex-1 min-w-0">
          🧠 {userName}
        </div>

        {/* global progress */}
        <div className="flex items-center gap-2 text-sm flex-shrink-0">
          <span className="text-gray-500 dark:text-gray-400 hidden sm:inline">Zvládnuto:</span>
          <span className="font-semibold text-gray-800 dark:text-gray-200">{ownedSkills}/{totalSkills}</span>
          <div className="w-28 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-500"
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <span className="text-gray-500 dark:text-gray-400 font-medium">{overallPct}%</span>
        </div>
      </div>

      {/* ══ BODY ═══════════════════════════════════════════════════════════ */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── LEFT SIDEBAR (tree navigator) ────────────────────────────── */}
        <div className="w-56 xl:w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Navigace
            </span>
          </div>
          <div className="flex-1 overflow-y-auto py-1 px-1 text-[13px]">
            {/* root entry */}
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer select-none text-sm font-medium
                ${navStack.length === 0
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400'
                }`}
              onClick={() => navTo(0)}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/>
              </svg>
              <span>Všechny znalosti</span>
            </div>

            {hierarchy.map(node => (
              <SidebarNode
                key={node._id}
                node={node}
                depth={0}
                selectedId={currentParent?._id ?? null}
                onSelect={sidebarSelect}
                skillMap={skillMap}
                expandedIds={expandedIds}
                onToggle={toggleExpanded}
              />
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL ──────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* breadcrumb + search toolbar */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-3 flex-wrap">

            {/* breadcrumb */}
            <nav className="flex items-center gap-0.5 text-sm flex-1 min-w-0 overflow-x-auto whitespace-nowrap">
              <button
                className="flex-shrink-0 text-blue-600 dark:text-blue-400 hover:underline font-medium"
                onClick={() => navTo(0)}
              >
                Znalosti
              </button>
              {navStack.map((node, i) => {
                const isLast = i === navStack.length - 1;
                return (
                  <React.Fragment key={node._id}>
                    <span className="flex-shrink-0 text-gray-400 mx-0.5"><ChevronRight size={12}/></span>
                    <button
                      className={`flex-shrink-0 font-medium truncate max-w-[160px]
                        ${isLast ? 'text-gray-700 dark:text-gray-200 pointer-events-none' : 'text-blue-600 dark:text-blue-400 hover:underline'}
                      `}
                      onClick={() => !isLast && navTo(i + 1)}
                    >
                      {node.name}
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>

            {/* action buttons for current folder */}
            {currentParent && !isReadOnly && (
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => openEdit(currentParent)}
                  className="text-xs px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/60 font-medium transition border border-blue-200 dark:border-blue-800"
                >
                  ✏️ Poznámka
                </button>
                <button
                  onClick={() => openAddChild(findNodeInTree(hierarchy, currentParent._id) || currentParent)}
                  className="text-xs px-2.5 py-1 rounded-lg bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/60 font-medium transition border border-green-200 dark:border-green-800"
                >
                  ➕ Přidat
                </button>
              </div>
            )}

            {/* search */}
            <div className="flex-shrink-0 relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Hledat v úrovni…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-7 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 w-44"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 text-base leading-none"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* current folder context strip */}
          {currentParent && (
            <div
              className="flex-shrink-0 px-4 py-1.5 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/60"
              style={{ background: (currentParent.color || '#8B5CF6') + '14' }}
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: currentParent.color || '#8B5CF6' }}/>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                {currentParent.name}
              </span>
              {currentParent.description && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden md:inline">
                  — {currentParent.description}
                </span>
              )}
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                {currentChildren.length} položek
              </span>
            </div>
          )}

          {/* cards grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredChildren.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 py-20">
                <svg className="w-14 h-14 mb-3 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <path d="M9 9h6M9 12h6M9 15h4"/>
                </svg>
                <p className="text-sm font-medium">{search ? 'Nic nenalezeno' : 'Tato složka je prázdná'}</p>
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="mt-2 text-xs text-blue-500 hover:underline"
                  >
                    Zrušit filtr
                  </button>
                )}
              </div>
            ) : (
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
              >
                {filteredChildren.map(node => (
                  <KnowledgeCard
                    key={node._id}
                    node={node}
                    hasSkill={skillMap[node._id] || false}
                    isReadOnly={isReadOnly}
                    onToggle={toggleSkill}
                    onDrillDown={drillDown}
                    skillMap={skillMap}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ EDIT SKILL MODAL ═══════════════════════════════════════════════ */}
      {editingNode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm">
            <h3 className="text-base font-bold mb-4 text-gray-900 dark:text-white">
              ✏️ {editingNode.name}
            </h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={editForm.hasSkill}
                  onChange={e => setEditForm(p => ({ ...p, hasSkill: e.target.checked }))}
                  className="w-4 h-4 rounded text-green-600 focus:ring-green-500"
                />
                Má znalost
              </label>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Poznámka:</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows="3"
                  placeholder="Volitelná poznámka…"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={saveEdit} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                  ✅ Uložit
                </button>
                <button onClick={() => setEditingNode(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition">
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ ADD CHILD MODAL ════════════════════════════════════════════════ */}
      {addingChild && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm">
            <h3 className="text-base font-bold mb-4 text-gray-900 dark:text-white">
              ➕ Pod: {addingChild.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Název:</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  placeholder="Název nové položky…"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && confirmAddChild()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Popis (volitelný):</label>
                <textarea
                  value={addForm.description}
                  onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
                  rows="2"
                  placeholder="Popis…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Barva:</label>
                <div className="flex flex-wrap gap-2">
                  {predefinedColors.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setAddForm(p => ({ ...p, color: c.value }))}
                      className={`w-6 h-6 rounded-full border-2 transition-transform ${addForm.color === c.value ? 'border-gray-900 dark:border-white scale-125' : 'border-transparent hover:scale-110'}`}
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={confirmAddChild}
                  disabled={!addForm.name.trim()}
                  className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  ✅ Přidat
                </button>
                <button
                  onClick={() => setAddingChild(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMindMapKnowledge;
